/**
 * Server state for the hub: the record with its options, the current cycle's
 * audit, and the mutations. One owner of `version`: the transition posts the
 * version the record was read with; a 409 refetches and says so; a 422
 * shows the engine's reason verbatim; success replaces the cache with the
 * returned record so the header re-renders from the server's answer. Every
 * other failure says what did not happen and offers Retry.
 */

import { statusLabel, type StatusEvent, type TransitionFlags } from '@beam/core/status';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api, reasonOf } from '@/app/api';
import type { AuditRow, MatterCycle, MatterDetail, OutstandingItem, SideStepKey, TransitionResponse, Appointment } from '@/app/types';

export const CONFLICT_TEXT = 'This case changed in another tab. Reloaded; check the status and try again.';

export function detailKey(ref: string) {
  return ['matter', ref] as const;
}
export function auditKey(ref: string) {
  return ['matter', ref, 'audit'] as const;
}

export function useMatterDetail(ref: string) {
  return useQuery({
    queryKey: detailKey(ref),
    queryFn: () => api<MatterDetail>(`/api/matters/${encodeURIComponent(ref)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 10_000,
  });
}

/**
 * Refresh the audit after a write. TanStack does not cancel a query's very
 * first fetch on invalidation (no data to fall back to), so a write that lands
 * while the log is still loading would otherwise keep the pre-write list for
 * the stale window; cancelling first makes the refetch real.
 */
export async function refreshAudit(client: QueryClient, ref: string): Promise<void> {
  await client.cancelQueries({ queryKey: auditKey(ref) });
  await client.invalidateQueries({ queryKey: auditKey(ref) });
}

export function useMatterAudit(ref: string) {
  return useQuery({
    queryKey: auditKey(ref),
    queryFn: () => api<{ events: AuditRow[] }>(`/api/matters/${encodeURIComponent(ref)}/audit`),
    select: (d) => d.events,
    staleTime: 10_000,
  });
}

export type TransitionVars = { event: StatusEvent['kind']; reason?: string; items?: Array<{ description: string; followUpAt: string }>; appointment?: { clinicId: string; startsAt: string; confirmed: boolean }; expectedVersion: number };

/** The toast after a move: the new chip, then the flags the engine raised, in words. */
export function movedText(to: number, flags: TransitionFlags, itemsAdded: number, from?: number): string {
  const parts = [`Moved to ${to} ${statusLabel(to as 1)}.`];
  const raised: string[] = [];
  if (flags.repeatDna) raised.push('Repeat DNA');
  if (flags.dnaFeeSuggested) raised.push('DNA fee suggested');
  if (flags.lateCancellation) raised.push('Late cancellation');
  if (raised.length > 0) parts.push(`${raised.join(' and ')} flagged.`);
  if (flags.skippedAppointment && from === 4) parts.push('No appointment: 5 Awaiting Appointment skipped.');
  if (itemsAdded > 0) parts.push(`${itemsAdded} outstanding ${itemsAdded === 1 ? 'item' : 'items'} added.`);
  return parts.join(' ');
}

/** "Nothing changed." with Retry: the shell's mutation-error toast (docs/ux/shell.md). */
function failed(message: string, retry: () => void): void {
  toast.error(`${message} Nothing changed.`, { action: { label: 'Retry', onClick: retry } });
}

export function useTransition(ref: string, callbacks: { onConflict?: () => void; onMoved?: (r: TransitionResponse) => void } = {}) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (vars: TransitionVars) => api<TransitionResponse>(`/api/matters/${encodeURIComponent(ref)}/transitions`, { method: 'POST', json: vars }),
    onSuccess: (data) => {
      const { moved, ...detail } = data;
      client.setQueryData(detailKey(ref), detail);
      void refreshAudit(client, ref);
      void client.invalidateQueries({ queryKey: ['matters'] });
      void client.invalidateQueries({ queryKey: ['pipeline'] });
      toast.success(movedText(moved.to, moved.flags, moved.itemsAdded, moved.from));
      callbacks.onMoved?.(data);
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) {
        await Promise.all([client.invalidateQueries({ queryKey: detailKey(ref) }), refreshAudit(client, ref)]);
        toast.error(CONFLICT_TEXT);
        callbacks.onConflict?.();
        return;
      }
      if (err instanceof ApiError && err.status === 422) {
        void client.invalidateQueries({ queryKey: detailKey(ref) });
        toast.error(`Could not move ${ref}: ${reasonOf(err) ?? 'the engine refused'}. Nothing changed.`);
        return;
      }
      // A retry re-sends the same version: a stale one is answered with the 409 above, never a second move.
      failed(`Could not move ${ref}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export function useSideStep(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (vars: { key: SideStepKey; value: boolean; label: string }) => api<MatterCycle>(`/api/matters/${encodeURIComponent(ref)}/side-steps`, { method: 'PUT', json: { key: vars.key, value: vars.value } }),
    onSuccess: (cycle) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, cycle } : prev));
      void refreshAudit(client, ref);
    },
    onError: (_err, vars) => {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      failed(`Could not save ${vars.label.toLowerCase()}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export function useAddItem(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (vars: { description: string; followUpAt: string }) => api<OutstandingItem>(`/api/matters/${encodeURIComponent(ref)}/items`, { method: 'POST', json: vars }),
    onSuccess: (item) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, items: [...prev.items, item] } : prev));
      void refreshAudit(client, ref);
    },
    onError: (_err, vars) => failed('Could not add the item.', () => mutation.mutate(vars)),
  });
  return mutation;
}

export function useResolveItem(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (itemId: string) => api<OutstandingItem>(`/api/matters/${encodeURIComponent(ref)}/items/${encodeURIComponent(itemId)}/resolve`, { method: 'POST' }),
    onSuccess: (item) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, items: prev.items.map((i) => (i.itemId === item.itemId ? item : i)) } : prev));
      void refreshAudit(client, ref);
    },
    onError: (_err, itemId) => failed('Could not resolve the item.', () => mutation.mutate(itemId)),
  });
  return mutation;
}

export function usePaymentReceived(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api<MatterCycle>(`/api/matters/${encodeURIComponent(ref)}/payment`, { method: 'PUT', json: { received: true } }),
    onSuccess: () => {
      // The options depend on the gate, so the whole record is reloaded.
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void refreshAudit(client, ref);
    },
    onError: () => failed('Could not record the payment.', () => mutation.mutate()),
  });
  return mutation;
}

/** A nurse confirms or unconfirms an appointment (docs/ux/clinic.md); the detail's appointment is patched in place. */
export function useConfirmAppointment(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (vars: { apptId: string; confirmed: boolean }) => api<Appointment>(`/api/matters/${encodeURIComponent(ref)}/appointments/${encodeURIComponent(vars.apptId)}`, { method: 'PUT', json: { confirmed: vars.confirmed } }),
    onSuccess: (appt) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, appointments: prev.appointments.map((a) => (a.apptId === appt.apptId ? appt : a)) } : prev));
      void refreshAudit(client, ref);
      void client.invalidateQueries({ queryKey: ['clinic'] });
    },
    onError: (_err, vars) => {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      failed(`Could not ${vars.confirmed ? 'confirm' : 'unconfirm'} the appointment.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

/** The Urgent flag, set or cleared from the hub's record (docs/ux/clinic.md). */
/**
 * The Urgent flag set or cleared from a row on any list (D-0066): the same
 * PUT and audit row as the hub's toggle, with the case named in the toast
 * because the list shows many cases.
 */
export function useSetUrgent() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ ref, urgent }: { ref: string; urgent: boolean }) => api<MatterCycle>(`/api/matters/${encodeURIComponent(ref)}/flags`, { method: 'PUT', json: { urgent } }),
    onSuccess: (cycle, { ref, urgent }) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, cycle, matter: { ...prev.matter, flags: { ...prev.matter.flags, urgent } } } : prev));
      void refreshAudit(client, ref);
      for (const key of [['matters'], ['pipeline'], ['qa'], ['dr'], ['clinic']]) void client.invalidateQueries({ queryKey: key });
      toast.success(urgent ? `${ref} marked urgent.` : `${ref}: Urgent flag cleared.`);
    },
    onError: (_err, vars) => {
      void client.invalidateQueries({ queryKey: detailKey(vars.ref) });
      failed(vars.urgent ? `Could not mark ${vars.ref} urgent.` : `Could not clear the Urgent flag on ${vars.ref}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export function useUrgent(ref: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (urgent: boolean) => api<MatterCycle>(`/api/matters/${encodeURIComponent(ref)}/flags`, { method: 'PUT', json: { urgent } }),
    onSuccess: (cycle, urgent) => {
      client.setQueryData<MatterDetail>(detailKey(ref), (prev) => (prev ? { ...prev, cycle, matter: { ...prev.matter, flags: { ...prev.matter.flags, urgent } } } : prev));
      void refreshAudit(client, ref);
      void client.invalidateQueries({ queryKey: ['matters'] });
      void client.invalidateQueries({ queryKey: ['pipeline'] });
      void client.invalidateQueries({ queryKey: ['qa'] });
      void client.invalidateQueries({ queryKey: ['dr'] });
      toast.success(urgent ? 'Marked urgent.' : 'Urgent flag cleared.');
    },
    onError: (_err, urgent) => {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      failed(urgent ? 'Could not mark the case urgent.' : 'Could not clear the Urgent flag.', () => mutation.mutate(urgent));
    },
  });
  return mutation;
}
